import { Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import {
    AudioPlayer,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
    Tip,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { IllustrationPenPaper } from '../../icons';
import { normalizeInput, useSyncedState } from '../../useSyncedState';

const page1BodyscanTextKey = 'module2Exercise3Page1BodyscanText';

export const exerciseData23: ExerciseData = {
    ExerciseView: Module2Exercise3,
    dataKeys: [page1BodyscanTextKey],
    questions: [
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Træn dig selv i at mærke din krop"
                    description="Ofte registerer kroppen presset, inden vores hoved gør, og det kan derfor være hjælpsomt at styrke din opmærksomhed og evne til at være tilstede i din krop. Det er mindfulness et godt redskab til."
                    answerTitle="Hvordan var det at sanse din krop?"
                />
            ),
            dataKeys: [page1BodyscanTextKey],
        },
    ],
};

function Module2Exercise3({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Kropsscanning">
            <ProgressBar current={exercise.CurrentPage} total={2} />
            <Switch
                cases={{
                    1: (
                        <Page1
                            bodyscanText={
                                exercise.Data[page1BodyscanTextKey] ?? ''
                            }
                            exercise={exercise}
                            onContinue={(bodyscanText) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1BodyscanTextKey]:
                                            normalizeInput(bodyscanText),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '2.3.completed',
                                });
                            }}
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

type Page1Props = {
    bodyscanText: string;
    exercise: ExerciseDto;
    onContinue: (bodyscanText: string) => void;
};

function Page1({
    bodyscanText: initialBodyscanText,
    exercise,
    onContinue,
}: Page1Props) {
    const [bodyscanText, setBodyscanText] =
        useSyncedState<string>(initialBodyscanText);

    return (
        <>
            <LabelDisplay2>Træn dig selv i at mærke din krop</LabelDisplay2>
            <LabelBody class="my-10">
                Ofte registerer kroppen presset, inden vores hoved gør, og det
                kan derfor være hjælpsomt at styrke din opmærksomhed og evne til
                at være tilstede i din krop. Det er mindfulness et godt redskab
                til.
            </LabelBody>
            <LabelBody>
                Denne mindfulnessøvelse guider og støtter dig i at være
                nysgerrig på din krops signaler. Find et roligt sted og tryk
                afspil, når du er klar.
            </LabelBody>
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/Lydfil-kropsscanning.mp3"
                duration="00:11:28"
                type="audio/mpeg"
                className="my-30"
            >
                <LabelBodySmall>Mindfulness</LabelBodySmall>
                <LabelBodyHeadline>Kropsscanning</LabelBodyHeadline>
            </AudioPlayer>
            <Card>
                <IllustrationPenPaper />
                <LabelDisplay2 class="mb-10 mt-20">
                    Hvordan gik det?
                </LabelDisplay2>
                <LabelBody>Hvordan var det at sanse din krop?</LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={bodyscanText}
                    onChangeText={setBodyscanText}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                class="mt-30"
                onClick={() => onContinue(bodyscanText)}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page2Props = { onContinue: () => void };
function Page2({ onContinue }: Page2Props) {
    return (
        <>
            <LabelDisplay2>Et godt redskab</LabelDisplay2>
            <LabelBody class="my-10">
                Ved at lave mindfulness styrker du din evne til at mærke dig
                selv og din krops signaler. Samtidig trænes dit systems bremse
                og det kan være en vej til at komme ned i gear.
            </LabelBody>
            <LabelBody>
                Husk på, at målet med mindfulness ikke er at forandre noget. Men
                at mærke det der er, på en ikke dømmende måde. Du er dig, og du
                gør det så godt du kan.
            </LabelBody>
            <Tip className="mt-30">
                Mindfulness kan være en god ting at øve sig flere gange på,
                eller gøre til en del af et dagligt ritual.
            </Tip>
            <LabelBody class="my-10">
                Hvis du har svært ved at sove, kan teknikken også være en hjælp,
                når du ligger i sengen.
            </LabelBody>
            <Spacer />
            <SecondaryButton class="mt-30" onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
